import React from 'react';
import { useLanguage } from '@/hooks/use-language';
import { 
  Shield, Droplets, Flame, Volume2, ShieldCheck, 
  Layout, Lock, Thermometer, Zap, Footprints, 
  Leaf, Sparkles, ArrowRight, Banknote, Gavel,
  Infinity, Grid, Maximize, RefreshCcw
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';

export const TechnologicalExcellence: React.FC<{ compact?: boolean; isWallPanel?: boolean; isCarpetTile?: boolean }> = ({ compact, isWallPanel, isCarpetTile }) => {
  const { t } = useLanguage();

  const standardAdvantages = [
    {
      icon: Shield,
      title: t('adv.durability.title'),
      desc: t('adv.durability.desc'),
      tag: '2000 kg/m³'
    },
    {
      icon: Droplets,
      title: t('adv.moisture.title'),
      desc: t('adv.moisture.desc'),
      tag: '100% H2O'
    },
    {
      icon: Flame,
      title: t('adv.fire.title'),
      desc: t('adv.fire.desc'),
      tag: 'KM2 Class'
    },
    {
      icon: Volume2,
      title: t('adv.acoustics.title'),
      desc: t('adv.acoustics.desc'),
      tag: '20 dB'
    },
    {
      icon: ShieldCheck,
      title: t('tech_wear_resistance_title') || t('adv.wear_resistance.title'),
      desc: t('adv.wear_resistance.desc'),
      tag: 'AC4/AC5'
    },
    {
      icon: Layout,
      title: t('adv.geometry_stability.title'),
      desc: t('adv.geometry_stability.desc'),
      tag: '±0.1 mm'
    },
    {
      icon: Lock,
      title: t('adv.lock_connection.title'),
      desc: t('adv.lock_connection.desc'),
      tag: 'UNICLIC®'
    },
    {
      icon: Thermometer,
      title: t('adv.underfloor_heating.title'),
      desc: t('adv.underfloor_heating.desc'),
      tag: 'до 27°C'
    },
    {
      icon: Zap,
      title: t('adv.impact_resistance.title'),
      desc: t('adv.impact_resistance.desc'),
      tag: 'High Impact'
    },
    {
      icon: Footprints,
      title: t('adv.anti_slip.title'),
      desc: t('adv.anti_slip.desc'),
      tag: 'R10'
    },
    {
      icon: Leaf,
      title: t('adv.eco_friendliness.title'),
      desc: t('adv.eco_friendliness.desc'),
      tag: 'E1'
    },
    {
      icon: Sparkles,
      title: t('adv.easy_care.title'),
      desc: t('adv.easy_care.desc'),
      tag: 'Easy Clean'
    }
  ];

  const wallPanelAdvantages = [
    {
      icon: Banknote,
      title: t('adv.spc_wall.economy.title'),
      desc: '',
      tag: 'FAST INSTALL'
    },
    {
      icon: Leaf,
      title: t('adv.spc_wall.eco.title'),
      desc: '',
      tag: 'E1'
    },
    {
      icon: Droplets,
      title: t('adv.spc_wall.water.title'),
      desc: '',
      tag: '100% H2O'
    },
    {
      icon: Gavel,
      title: t('adv.spc_wall.impact.title'),
      desc: '',
      tag: 'HIGH IMPACT'
    },
    {
      icon: Infinity,
      title: t('adv.spc_wall.durability.title'),
      desc: '',
      tag: 'LONG LIFE'
    },
    {
      icon: Flame,
      title: t('adv.spc_wall.fire.title'),
      desc: '',
      tag: 'CLASS B'
    },
    {
      icon: Grid,
      title: t('adv.spc_wall.no_grout.title'),
      desc: '',
      tag: 'NO GROUT'
    },
    {
      icon: Maximize,
      title: t('adv.spc_wall.large_format.title'),
      desc: '',
      tag: 'LARGE FORMAT'
    },
    {
      icon: RefreshCcw,
      title: t('adv.spc_wall.renovation.title'),
      desc: '',
      tag: 'RENOVATION'
    }
  ];

  const carpetTileAdvantages = [
    {
      icon: ShieldCheck,
      title: t('carpet.adv.wear.title'),
      desc: '',
      tag: t('carpet.adv.wear.tag')
    },
    {
      icon: Flame,
      title: t('carpet.adv.fire.title'),
      desc: '',
      tag: t('carpet.adv.fire.tag')
    },
    {
      icon: Grid,
      title: t('carpet.adv.modular.title'),
      desc: '',
      tag: t('carpet.adv.modular.tag')
    },
    {
      icon: RefreshCcw,
      title: t('carpet.adv.replacement.title'),
      desc: '',
      tag: t('carpet.adv.replacement.tag')
    },
    {
      icon: Sparkles,
      title: t('carpet.adv.color.title'),
      desc: '',
      tag: t('carpet.adv.color.tag')
    },
    {
      icon: Layout,
      title: t('carpet.adv.backing.title'),
      desc: '',
      tag: t('carpet.adv.backing.tag')
    }
  ];

  const advantages = isWallPanel ? wallPanelAdvantages : (isCarpetTile ? carpetTileAdvantages : standardAdvantages);

  const carouselItems = [...advantages, ...advantages];

  return (
    <section className={cn(
      "relative overflow-hidden",
      compact ? "pt-10 lg:pt-16 pb-6 lg:pb-10" : "py-16 lg:py-24"
    )}>
      <div className="grid-cells opacity-50" />
      <div className="container-custom relative z-10">
        <div className={cn(
          "text-center space-y-4",
          compact ? "mb-8 lg:mb-10" : "mb-12 lg:mb-16"
        )}>
          <h2 className={cn(
            "font-extrabold uppercase tracking-tighter",
            compact ? "text-2xl lg:text-3xl" : "text-3xl lg:text-4xl"
          )}>{t('home.advantages.title')}</h2>
          <p className="text-white/40 uppercase tracking-[0.2em] text-[10px] lg:text-xs">
            {isWallPanel 
              ? t('wallPanels.techAdv.subtitle') 
              : (isCarpetTile ? t('carpet.tech.subtitle') : t('home.advantages.subtitle'))
            }
          </p>
        </div>
      </div>

      <div className="marquee-container">
        <div className="marquee-content">
          {[...carouselItems, ...carouselItems].map((adv, idx) => (
            <div
              key={idx}
              className={cn(
                "mirror-card tech-excellence-card hover:border-primary/30 group shrink-0 transition-all duration-500",
                compact 
                  ? "" 
                  : ""
              )}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: compact ? 'clamp(0.5rem, 0.75vw, 1rem)' : 'clamp(0.75rem, 1vw, 1rem)',
                padding: compact ? 'clamp(1rem, 1.5vw, 1.5rem)' : 'clamp(1rem, 2.5vw, 2.5rem)',
                width: compact ? 'clamp(140px, 15vw, 240px)' : 'clamp(140px, 22vw, 350px)'
              }}
            >
              <div 
                className="flex items-center justify-center bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-colors duration-500"
                style={{
                  width: compact ? 'clamp(1.5rem, 2.25vw, 2.25rem)' : 'clamp(1.5rem, 3vw, 3rem)',
                  height: compact ? 'clamp(1.5rem, 2.25vw, 2.25rem)' : 'clamp(1.5rem, 3vw, 3rem)'
                }}
              >
                <adv.icon size={compact ? 12 : 16} />
              </div>
              <h3 
                className="font-bold uppercase tracking-tight"
                style={{ fontSize: compact ? 'clamp(0.625rem, 0.875vw, 0.875rem)' : 'clamp(0.75rem, 1.25vw, 1.25rem)' }}
              >{adv.title}</h3>
              {compact ? null : <p className="text-white/40 leading-relaxed min-h-[3em] hidden sm:block" style={{ fontSize: 'clamp(0.625rem, 0.75vw, 0.75rem)' }}>{adv.desc}</p>}
              <div className="flex items-center" style={{ paddingTop: compact ? 'clamp(0.15rem, 0.5vw, 0.5rem)' : 'clamp(0.25rem, 1vw, 1rem)' }}>
                <span 
                  className="tech-card__value"
                  style={{ fontSize: compact ? 'clamp(0.5rem, 0.75vw, 0.75rem)' : 'clamp(0.625rem, 0.875vw, 0.875rem)' }}
                >{adv.tag}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {!compact && (
        <div className="container-custom relative z-10 mt-12 lg:mt-16 flex justify-center">
          <Link to="/useful" className="inline-flex items-center gap-2 text-primary uppercase tracking-widest text-[10px] lg:text-xs font-bold hover:gap-4 hover:brightness-110 hover:underline underline-offset-4 decoration-primary/30 transition-all duration-300">
            {t('home.advantages.go_to_useful')} <ArrowRight size={16} />
          </Link>
        </div>
      )}
    </section>
  );
};
